import { Timeline } from 'antd';
import { PlusCircleTwoTone, MinusCircleTwoTone } from '@ant-design/icons';
import { palette } from '@shipium-corp/shipium-ui-theme';
import { useStoreState, useStoreActions } from '../../store';
import { format } from 'date-fns/format';
import { formatDuration } from 'date-fns/formatDuration';
import { intervalToDuration } from 'date-fns/intervalToDuration';
import { useEffect } from 'react';
import { createUseStyles } from 'react-jss';
import logo from '~/assets/symbol.png';
import { ShipiumTag } from '../../components/shared/ShipiumTag';
import { ConfigurationCard } from '../../components/shared/ConfigurationCard';
import type { SimulationDetail } from '../../store/simulation/types';
import { isShipiumEmail } from '~/utils/regex';

export interface SimulationTimelineProps {
  title?: string;
  simulation: SimulationDetail;
}

const useStyles = createUseStyles({
  timelineStyle: {
    marginTop: '20px',
    marginBottom: '20px',
    marginRight: '20px',
    paddingInline: '8px',
  },
  timelineItemStyle: {
    '&& .ant-comment > .ant-comment-inner': {
      padding: 0,
    },
    paddingBottom: 28,
  },
  shipiumIcon: {
    marginLeft: 5,
  },
  eventDetailStyle: {
    fontSize: 12,
  },
  assetNameStyle: {
    fontWeight: 'bold',
    marginBottom: 3,
  },
  eventDateStyle: {
    color: 'rbga(0,0,0, 0.65)',
  },
  scrollContainer: {
    display: 'flex',
    flexFlow: 'column',
    height: 'calc(100vh - 92px)',
    overflow: 'auto',
    paddingBottom: 5,
  },
});

export const SimulationTimeline = ({ title = 'Simulation Events', simulation }: SimulationTimelineProps) => {
  const events = simulation.simulationEvents.sort(
    (a, b) => new Date(b.eventTimestamp).getTime() - new Date(a.eventTimestamp).getTime(),
  );
  const fetchUsers = useStoreActions((actions) => actions.users.fetchUsers);
  const users = useStoreState((state) => state.users.users);
  const classes = useStyles();

  useEffect(() => {
    fetchUsers(undefined);
  }, [fetchUsers]);

  const timelineItems = events?.map((event) => {
    const interval = intervalToDuration({
      start: new Date(event.eventTimestamp),
      end: new Date(),
    });
    const eventAge =
      interval.years || interval.days || interval.months || interval.minutes
        ? formatDuration(interval, { format: ['years', 'months', 'days', 'hours', 'minutes'] })
        : 'Less than a minute';

    const isCreationEvent = event.simulationEventType.includes('Created');
    const dot = isCreationEvent ? (
      <PlusCircleTwoTone twoToneColor={palette.Teal.light} />
    ) : (
      <MinusCircleTwoTone twoToneColor={palette.Gold.normal} />
    );
    const eventTypeTag = isCreationEvent ? (
      <ShipiumTag style={{ marginLeft: 4, fontSize: 10, padding: 4, lineHeight: '10px' }} color="Teal">
        Created
      </ShipiumTag>
    ) : (
      <ShipiumTag style={{ marginLeft: 4, fontSize: 10, padding: 4, lineHeight: '10px' }} color="Gold">
        Updated
      </ShipiumTag>
    );
    const userId = event.eventCreatedBy;

    const eventDetail = (
      <div className={classes.eventDetailStyle}>
        <div className={classes.assetNameStyle}>{event.simulationEventDetail}</div>
        <div>{format(new Date(event.eventTimestamp), 'Pp')}</div>
        <div>
          <span>
            {users[userId]?.givenName} {users[userId]?.surname}
            <img
              src={logo}
              hidden={!isShipiumEmail(users[userId]?.email)}
              width="20px"
              alt="Shipium User"
              className={classes.shipiumIcon}
            />
          </span>
        </div>
      </div>
    );
    return (
      <Timeline.Item
        dot={dot}
        className={classes.timelineItemStyle}
        label={
          <div>
            <div>{eventTypeTag}</div>
            <div style={{ fontSize: 12 }}>{eventAge} ago</div>
          </div>
        }
      >
        {eventDetail}
      </Timeline.Item>
    );
  });

  return (
    <ConfigurationCard heading={title} headingCount={timelineItems?.length} bodyStyle={{ padding: 0 }}>
      <div className={classes.scrollContainer}>
        <Timeline mode={'left'} className={classes.timelineStyle}>
          {timelineItems}
        </Timeline>
      </div>
    </ConfigurationCard>
  );
};
